import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GenericTextField from '../../../../components/GenericTextField/GenericTextField';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import BillingLinesEditor, { billingMonthly } from '../../../../components/Billing/BillingLinesEditor';
import { discountAmounts, parseDiscountValue } from '../../../../utilities/helper/discount.helper';
import { DiscountModeEnum } from '../../../../common/enums/invoicing.enum';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { FINANCE_SETTINGS } from '../../../../common/constants/routes.constants';
import { BillingSetupSubTab } from '../../../../common/enums/common.enum';
import { BillingItemTypeEnum, BillingSourceEnum, ContractMissingEnum } from '../../../../common/enums/invoicing.enum';
import { BillingSchemeDto, ChildContractDto } from '../../../../common/interfaces/invoicing.interface';
import {
  closeChildBillingProfile,
  getBillingScheme,
  getNextContractNumber,
  setContractNumber,
  setInvoiceRecipients,
  setPayer
} from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { calculateLineTotals, formatMoney } from '../../../../utilities/helper/money.helper';
import { toLocalDateString } from '../../../../utilities/helper/date.helper';
import { useChildBillingProfileEditor } from '../useChildBillingProfileEditor';
import { invoiceNumber, issuedRange, missingList, placementText } from './contractText';

import { palette } from '../../../../theme';
export type ContractSection = 'number' | 'payer' | 'price';
export type PriceMode = 'group' | 'own';

interface ContractDrawerProps {
  kindergartenId: string;
  contract: ChildContractDto;
  vatApplicable: boolean;
  /** Scrolled to on open, for the quick fixes that lead here. */
  focus?: ContractSection;
  /** Overrides where the price section starts, e.g. "Bill differently" on the Billing tab. */
  initialPriceMode?: PriceMode;
  onClose: () => void;
  /** Called after anything was written, including a save that failed partway. */
  onSaved: () => void;
}

const BRAND = palette.primary.main;
// readiness comes from the theme, so a step and a status chip agree on what green and orange mean
const DONE = 'success.main';
const TODO = 'warning.main';
const MUTED = palette.text.secondary;
const TINT = palette.surface.tint;

const Section = React.forwardRef<HTMLDivElement, { title: string; description: string; children: React.ReactNode }>(
  ({ title, description, children }, ref) => (
    <Box ref={ref} component="section" aria-label={title} sx={{ scrollMarginTop: 16 }}>
      <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 14, color: MUTED, mb: 1.5 }}>{description}</Typography>
      {children}
    </Box>
  )
);
Section.displayName = 'Section';

/**
 * A text input with its label above it, as the panel is drawn. The floating label of the
 * app's usual field sits inside the outline, which in a form this dense reads as a value.
 */
const LabeledInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}> = ({ label, value, onChange, placeholder, disabled, inputRef }) => {
  const id = useId();
  return (
    <Box>
      <Typography component="label" htmlFor={id} sx={{ display: 'block', fontSize: 14, color: MUTED, mb: 0.75 }}>
        {label}
      </Typography>
      <TextField
        id={id}
        fullWidth
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        inputRef={inputRef}
        onChange={event => onChange(event.target.value)}
        sx={{ '& .MuiOutlinedInput-input': { py: '10px', px: '14px', fontSize: 16 } }}
      />
    </Box>
  );
};

/** "1. 9. 2026." - how Serbian writes a date, and what the design shows. */
const formatDate = (value: string) => new Intl.DateTimeFormat('sr-Latn-RS').format(new Date(`${value}T00:00:00`));

/**
 * Everything a child needs before it can be invoiced, on one panel: the contract number,
 * the parent who pays, and what it is charged.
 *
 * These used to live on three screens, and the price was the one nobody saw missing until a
 * generated invoice came out empty. The step row at the top is the panel's own answer to
 * "is this child ready", recomputed as the form is filled, so it can disagree with the list
 * behind it only until Save.
 *
 * Save writes only what changed, and an incomplete contract is allowed: a number without a
 * payer yet is still progress, and the list keeps saying what is left.
 */
const ContractDrawer: React.FC<ContractDrawerProps> = ({
  kindergartenId,
  contract,
  vatApplicable,
  focus,
  initialPriceMode,
  onClose,
  onSaved
}) => {
  const { t } = useTranslation();
  const today = toLocalDateString(new Date());
  const editor = useChildBillingProfileEditor(kindergartenId, contract.childId, today);

  const originalPayer = contract.parents.find(parent => parent.isPayer) ?? null;
  const [number, setNumber] = useState(contract.contractNumber ?? '');
  // the number is on documents parents already hold; the API refuses to change it
  const numberLocked = contract.lastInvoiceSeq > 0 && !!contract.contractNumber;
  const [parentId, setParentId] = useState(originalPayer?.parentId ?? '');
  const [address, setAddress] = useState(originalPayer?.payerAddress ?? '');
  const [toAllParents, setToAllParents] = useState(contract.invoiceToAllParents);
  const [priceMode, setPriceMode] = useState<PriceMode>(
    initialPriceMode ?? (contract.billingSource === BillingSourceEnum.CHILD_PROFILE ? 'own' : 'group')
  );
  const [backToGroupFrom, setBackToGroupFrom] = useState(today);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [groupScheme, setGroupScheme] = useState<BillingSchemeDto | null>(null);
  const [groupSchemeLoading, setGroupSchemeLoading] = useState(!!contract.groupBillingSchemeId);
  const [saving, setSaving] = useState(false);

  const sections = {
    number: useRef<HTMLDivElement>(null),
    payer: useRef<HTMLDivElement>(null),
    price: useRef<HTMLDivElement>(null)
  };
  const numberInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!focus) return;
    // jsdom has no scrollIntoView; a browser always does
    sections[focus].current?.scrollIntoView?.({ block: 'start' });
    if (focus === 'number') numberInput.current?.focus();
    // only on open: refocusing as the admin types would fight them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (contract.contractNumber || !contract.membershipId) return;
    getNextContractNumber(kindergartenId)
      .then(result => setSuggestion(result.results?.suggestion ?? null))
      // a suggestion is a convenience; without one the field still works
      .catch(() => undefined);
  }, [kindergartenId, contract.contractNumber, contract.membershipId]);

  useEffect(() => {
    if (!contract.groupBillingSchemeId) return;
    setGroupSchemeLoading(true);
    getBillingScheme(kindergartenId, contract.groupBillingSchemeId)
      .then(result => setGroupScheme(result.results ?? null))
      .catch(() => createNotification(MESSAGE_TYPE.ERROR, t('contracts.drawer.price.groupListFailed')))
      .finally(() => setGroupSchemeLoading(false));
  }, [kindergartenId, contract.groupBillingSchemeId, t]);

  const enrolled = !!contract.membershipId;
  const trimmedNumber = number.trim();
  const selectedParent = contract.parents.find(parent => parent.parentId === parentId) ?? null;
  // the copies go to everyone but the payer, and only where there is an address to send to
  const otherParents = contract.parents.filter(parent => parent.parentId !== parentId);
  const othersWithEmail = otherParents.filter(parent => !!parent.email).length;
  const ownPriced = editor.lines.length > 0 || (!!editor.current?.billingSchemeId && !editor.isDirty);
  const priced = priceMode === 'group' ? enrolled && !!contract.groupBillingSchemeId : ownPriced;

  const steps: { key: ContractMissingEnum; label: string; done: boolean }[] = [
    { key: ContractMissingEnum.MEMBERSHIP, label: t('contracts.drawer.steps.membership'), done: enrolled },
    { key: ContractMissingEnum.CONTRACT_NUMBER, label: t('contracts.drawer.steps.number'), done: enrolled && !!trimmedNumber },
    { key: ContractMissingEnum.PAYER, label: t('contracts.drawer.steps.payer'), done: !!selectedParent },
    { key: ContractMissingEnum.PRICE, label: t('contracts.drawer.steps.price'), done: priced }
  ];
  const stillMissing = steps.filter(step => !step.done).map(step => step.key);
  const ready = stillMissing.length === 0;

  /** The fixed monthly figure and the per-day items that come on top of it. */
  const monthly = useMemo((): { amount: number | null; perDay: string[]; discounted?: boolean } => {
    if (priceMode === 'group') {
      if (!enrolled || !groupScheme) return { amount: null, perDay: [] };
      return {
        amount: groupScheme.monthlyFixedNet,
        perDay: groupScheme.items.filter(item => item.type === BillingItemTypeEnum.ATTENDANCE_BASED).map(item => item.pdfLabel || item.name)
      };
    }
    if (editor.lines.length) {
      // discounts on fixed items are in it; those on per-day items are not, like the items
      const figure = billingMonthly(editor.lines, editor.discounts, false);
      return { amount: figure.net, perDay: figure.perDay, discounted: figure.discount > 0 };
    }
    // priced by a scheme the arrangement names: the list already resolved its figure
    if (ownPriced) return { amount: contract.monthlyFixedNet, perDay: [] };
    return { amount: null, perDay: [] };
  }, [priceMode, enrolled, groupScheme, editor.lines, editor.discounts, ownPriced, contract.monthlyFixedNet]);

  /** The discounts as the preview lists them: what each takes off, or its percentage per day. */
  const discountRows =
    priceMode === 'own'
      ? editor.lines.flatMap(line => {
          const own = editor.discounts.filter(discount => discount.appliesTo === line.billingItemId);
          const perDay = line.type === BillingItemTypeEnum.ATTENDANCE_BASED;
          const amounts = discountAmounts(
            calculateLineTotals(line.unitPriceNetOverride ?? line.catalogPrice, line.quantity ?? 1).net,
            own.map(discount => ({ mode: discount.mode, value: parseDiscountValue(discount.valueText) }))
          );
          return own.map((discount, index) => ({
            key: discount.key,
            label: discount.label.trim() || t('discounts.unnamed'),
            text:
              perDay && discount.mode === DiscountModeEnum.PERCENT
                ? `−${discount.valueText.trim() || 0} %`
                : `−${formatMoney(perDay ? (parseDiscountValue(discount.valueText) ?? 0) : amounts[index])}`
          }));
        })
      : [];

  const monthlyText =
    monthly.amount === null
      ? '—'
      : [
          formatMoney(monthly.amount, { withCurrency: true }),
          monthly.perDay.length ? t('contracts.drawer.preview.plusAttendance', { items: monthly.perDay.join(', ').toLowerCase() }) : ''
        ]
          .filter(Boolean)
          .join(' ');

  const settingsHref = (schemeId?: string | null) =>
    `${FINANCE_SETTINGS}?kindergartenId=${kindergartenId}&tab=${BillingSetupSubTab.Schemes}${schemeId ? `&schemeId=${schemeId}` : ''}`;

  const handleSave = async () => {
    const numberChanged = enrolled && !!trimmedNumber && trimmedNumber !== (contract.contractNumber ?? '');
    const payerChanged = !!parentId && (parentId !== (originalPayer?.parentId ?? '') || address !== (originalPayer?.payerAddress ?? ''));
    const recipientsChanged = toAllParents !== contract.invoiceToAllParents;
    const ownChanged = priceMode === 'own' && editor.isDirty;
    // back to the group: the open arrangement is ended, never deleted
    const endOwn = priceMode === 'group' && !!editor.current;

    if (ownChanged) {
      const problem = editor.validationError();
      if (problem) {
        createNotification(MESSAGE_TYPE.ERROR, t(problem));
        return;
      }
    }

    const writes: (() => Promise<unknown>)[] = [];
    // the contract belongs to a specific membership: after a group change the new one often
    // starts in the future, so it is named rather than left to the backend to guess
    if (numberChanged) writes.push(() => setContractNumber(kindergartenId, contract.childId, trimmedNumber, contract.membershipId!));
    if (payerChanged) writes.push(() => setPayer(kindergartenId, contract.childId, parentId, address.trim() || undefined));
    if (recipientsChanged) writes.push(() => setInvoiceRecipients(kindergartenId, contract.childId, toAllParents));
    if (ownChanged) writes.push(() => editor.save());
    if (endOwn) writes.push(() => closeChildBillingProfile(kindergartenId, contract.childId, backToGroupFrom));

    if (!writes.length) {
      createNotification(MESSAGE_TYPE.SUCCESS, t('contracts.drawer.saved.nothingChanged'));
      onClose();
      return;
    }

    setSaving(true);
    let written = 0;
    try {
      // one after another: a refused contract number should not leave a payer half-saved
      // behind a message that says the save failed
      for (const write of writes) {
        await write();
        written += 1;
      }
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('contracts.saveFailed'));
      if (written) onSaved();
      setSaving(false);
      return;
    }
    setSaving(false);
    createNotification(
      MESSAGE_TYPE.SUCCESS,
      ready
        ? t('contracts.drawer.saved.ready', { name: contract.childFullName })
        : t('contracts.drawer.saved.incomplete', { name: contract.childFullName, what: missingList(t, stillMissing) })
    );
    onSaved();
    onClose();
  };

  const headerLine = [
    placementText(t, contract),
    contract.enrolledFrom ? t('contracts.drawer.enrolledSince', { date: formatDate(contract.enrolledFrom) }) : null
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-label': t('contracts.drawer.ariaLabel', { name: contract.childFullName }),
          // the theme paints every Drawer as the purple navigation sidebar; this one is a panel
          sx: {
            width: 720,
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: palette.surface.main,
            color: 'text.primary',
            borderRight: 'none'
          }
        } as object,
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,.5)' } }
      }}
    >
      {/* header */}
      <Box sx={{ p: '24px 24px 16px', borderBottom: `1px solid ${palette.line}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, color: MUTED }}>{t('contracts.title')}</Typography>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600 }}>
              {contract.childFullName}
            </Typography>
            <Typography sx={{ fontSize: 14, color: MUTED }}>{headerLine}</Typography>
          </Box>
          <IconButton aria-label={t('common.close')} onClick={onClose} sx={{ mt: -0.5, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box
          component="ol"
          aria-label={t('contracts.drawer.steps.ariaLabel')}
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, listStyle: 'none', p: 0, m: 0, mt: 2 }}
        >
          {steps.map((step, index) => (
            <Box
              component="li"
              key={step.key}
              aria-label={t(step.done ? 'contracts.drawer.steps.done' : 'contracts.drawer.steps.todo', { step: step.label })}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: step.done ? DONE : TODO,
                  color: palette.common.white,
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {step.done ? <CheckIcon sx={{ fontSize: 14 }} /> : index + 1}
              </Box>
              <Typography aria-hidden sx={{ fontSize: 12, color: step.done ? palette.text.primary : TODO }} noWrap>
                {step.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* body */}
      <Box sx={{ p: 3, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
        <Section ref={sections.number} title={t('contracts.drawer.number.title')} description={t('contracts.drawer.number.description')}>
          {!enrolled ? (
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 13 }}>
              {t('contracts.drawer.number.noMembership')}
            </Alert>
          ) : (
            <>
              <LabeledInput
                label={t('contracts.number')}
                value={number}
                // invoice numbers are <contract>-NN, so once one is issued the server refuses a change
                disabled={numberLocked}
                inputRef={numberInput}
                placeholder={t('contracts.drawer.number.placeholder', { example: suggestion ?? `UG-${today.slice(0, 4)}/019` })}
                onChange={setNumber}
              />
              <Box sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>
                {numberLocked ? null : trimmedNumber ? (
                  t('contracts.drawer.number.invoicesHint', { number: trimmedNumber })
                ) : suggestion ? (
                  <>
                    {t('contracts.drawer.number.suggestion', { suggestion })}{' '}
                    <Link component="button" type="button" onClick={() => setNumber(suggestion)} sx={{ fontSize: 12, color: BRAND, verticalAlign: 'baseline' }}>
                      {t('contracts.drawer.number.useSuggestion')}
                    </Link>
                  </>
                ) : null}
              </Box>
              {numberLocked && contract.contractNumber && (
                <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13, mt: 1.5 }}>
                  {t('contracts.drawer.number.issuedLocked', {
                    count: contract.lastInvoiceSeq,
                    issued: issuedRange(t, contract.contractNumber, contract.lastInvoiceSeq)
                  })}
                </Alert>
              )}
            </>
          )}
        </Section>

        <Section ref={sections.payer} title={t('contracts.drawer.payer.title')} description={t('contracts.drawer.payer.description')}>
          {!contract.parents.length ? (
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 13 }}>
              {t('contracts.noLinkedParents')}
            </Alert>
          ) : (
            <>
              <RadioGroup
                aria-label={t('contracts.drawer.payer.title')}
                value={parentId}
                onChange={(_, value) => {
                  setParentId(value);
                  // the address belongs to the parent, so it follows the choice
                  setAddress(contract.parents.find(parent => parent.parentId === value)?.payerAddress ?? '');
                }}
                sx={{ gap: 1 }}
              >
                {contract.parents.map(parent => {
                  const selected = parent.parentId === parentId;
                  return (
                    <FormControlLabel
                      key={parent.parentId}
                      value={parent.parentId}
                      control={<Radio sx={{ '&.Mui-checked': { color: BRAND } }} />}
                      label={
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{parent.fullName}</Typography>
                          {parent.email && <Typography sx={{ fontSize: 12, color: MUTED }}>{parent.email}</Typography>}
                        </Box>
                      }
                      sx={{
                        m: 0,
                        // a 2px border on the chosen card, padding trimmed by the extra pixel so
                        // the cards do not shift as the choice moves
                        p: selected ? '11px 15px' : '12px 16px',
                        borderRadius: '10px',
                        border: selected ? `2px solid ${BRAND}` : `1px solid ${palette.field.border}`,
                        backgroundColor: selected ? TINT : palette.surface.main
                      }}
                    />
                  );
                })}
              </RadioGroup>
              <Box sx={{ mt: 2 }}>
                <LabeledInput
                  label={t('contracts.drawer.payer.address')}
                  placeholder={t('contracts.drawer.payer.addressPlaceholder')}
                  value={address}
                  onChange={setAddress}
                  disabled={!parentId}
                />
              </Box>
              {/* with one parent there is nobody to copy */}
              {contract.parents.length > 1 && (
                <Box sx={{ mt: 1.5 }}>
                  <FormControlLabel
                    control={<Checkbox checked={toAllParents} onChange={event => setToAllParents(event.target.checked)} />}
                    label={<Typography sx={{ fontSize: 14 }}>{t('contracts.drawer.payer.allParents')}</Typography>}
                  />
                  <Typography sx={{ fontSize: 12, color: MUTED, pl: 4 }}>
                    {selectedParent
                      ? t('contracts.drawer.payer.allParentsHint', { name: selectedParent.fullName })
                      : t('contracts.drawer.payer.allParentsHintNoPayer')}
                  </Typography>
                  {toAllParents && othersWithEmail < otherParents.length && (
                    <Typography sx={{ fontSize: 12, color: TODO, pl: 4 }}>
                      {othersWithEmail
                        ? t('contracts.drawer.payer.allParentsSomeEmail', { count: othersWithEmail, total: otherParents.length })
                        : t('contracts.drawer.payer.allParentsNoEmail')}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Section>

        <Section ref={sections.price} title={t('contracts.drawer.price.title')} description={t('contracts.drawer.price.description')}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={priceMode}
            onChange={(_, value: PriceMode | null) => value && setPriceMode(value)}
            sx={{
              backgroundColor: TINT,
              p: 0.5,
              borderRadius: '10px',
              mb: 2,
              '& .MuiToggleButton-root': {
                border: 0,
                borderRadius: '8px !important',
                textTransform: 'none',
                fontSize: 14,
                color: MUTED,
                '&.Mui-selected': { backgroundColor: palette.surface.main, color: BRAND, fontWeight: 600, '&:hover': { backgroundColor: palette.surface.main } }
              }
            }}
          >
            <ToggleButton value="group">{t('contracts.drawer.price.byGroup')}</ToggleButton>
            <ToggleButton value="own">{t('contracts.drawer.price.own')}</ToggleButton>
          </ToggleButtonGroup>

          {priceMode === 'group' ? (
            !enrolled ? (
              <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 13 }}>
                {t('contracts.drawer.price.notEnrolled')}
              </Alert>
            ) : !contract.groupBillingSchemeId ? (
              <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 2 }}>
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    {t('contracts.drawer.price.groupHasNone', { name: contract.groupName ?? '' })}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: MUTED }}>{t('contracts.drawer.price.groupHasNoneHint')}</Typography>
                </Box>
                <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderTop: `1px solid ${palette.line}`, fontSize: 12 }}>
                  <Typography sx={{ fontSize: 12, color: TODO }}>{t('contracts.drawer.price.emptyInvoice')}</Typography>
                  <Link component={RouterLink} to={settingsHref()} sx={{ fontSize: 12, color: BRAND }}>
                    {t('contracts.drawer.price.addPriceList')}
                  </Link>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 2 }}>
                {groupSchemeLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  (groupScheme?.items ?? []).map(item => (
                    <Stack
                      key={item.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ px: 2, py: 1.25, '& + &': { borderTop: `1px solid ${palette.line}` } }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 14 }}>{item.pdfLabel || item.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>
                          {item.type === BillingItemTypeEnum.ATTENDANCE_BASED
                            ? t('contracts.drawer.price.perDay')
                            : t('contracts.drawer.price.fixedMonthly')}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(
                          item.type === BillingItemTypeEnum.ATTENDANCE_BASED
                            ? item.unitPriceNet
                            : calculateLineTotals(item.unitPriceNet, item.quantity).net,
                          { withCurrency: true }
                        )}
                      </Typography>
                    </Stack>
                  ))
                )}
                <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderTop: `1px solid ${palette.line}` }}>
                  <Typography sx={{ fontSize: 12, color: MUTED }}>{t('contracts.drawer.price.groupFooter')}</Typography>
                  <Link component={RouterLink} to={settingsHref(contract.groupBillingSchemeId)} sx={{ fontSize: 12, color: BRAND }}>
                    {t('contracts.drawer.price.editGroupList')}
                  </Link>
                </Stack>
              </Box>
            )
          ) : editor.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {editor.current?.billingSchemeName && !editor.current.items.length && (
                <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13, mb: 2 }}>
                  {t('contracts.drawer.price.ownByScheme', { name: editor.current.billingSchemeName })}
                </Alert>
              )}
              <Typography sx={{ fontSize: 13, color: MUTED, mb: 1.5 }}>{t('discounts.contractHint')}</Typography>
              <BillingLinesEditor
                lines={editor.lines}
                onChange={editor.setLines}
                catalog={editor.catalog}
                vatApplicable={vatApplicable}
                inactiveNotice={t('billing.profile.inactiveNotice')}
                showTotal={false}
                discounts={editor.discounts}
                onDiscountsChange={editor.setDiscounts}
                showErrors={editor.showErrors}
              />
              <GenericTextField
                label={t('contracts.drawer.price.validFrom')}
                isDate
                maxDate={null}
                value={editor.validFrom}
                onChange={event => editor.setValidFrom(event.target.value)}
                helperText={t('contracts.drawer.price.validFromHint')}
                sx={{ mt: 2 }}
              />
            </>
          )}

          {/* leaving the child's own price is a dated end, so the date is asked for here */}
          {priceMode === 'group' && editor.current && (
            <GenericTextField
              label={t('contracts.drawer.price.backToGroupFrom')}
              isDate
              maxDate={null}
              value={backToGroupFrom}
              onChange={event => setBackToGroupFrom(event.target.value)}
              helperText={t('contracts.drawer.price.backToGroupHint')}
              sx={{ mt: 2 }}
            />
          )}
        </Section>

        <Box component="section" aria-label={t('contracts.drawer.preview.title')} sx={{ backgroundColor: TINT, borderRadius: 4, p: '16px 20px' }}>
          <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
            {t('contracts.drawer.preview.title')}
          </Typography>
          <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 0.75, m: 0, fontSize: 14 }}>
            <Box component="dt" sx={{ color: MUTED }}>
              {t('contracts.drawer.preview.number')}
            </Box>
            <Box component="dd" sx={{ m: 0 }}>
              {enrolled && trimmedNumber ? (
                invoiceNumber(trimmedNumber, contract.lastInvoiceSeq + 1)
              ) : (
                <Box component="span" sx={{ color: MUTED }}>
                  {t('contracts.drawer.preview.numberPending')}
                </Box>
              )}
            </Box>
            <Box component="dt" sx={{ color: MUTED }}>
              {t('contracts.drawer.preview.addressedTo')}
            </Box>
            <Box component="dd" sx={{ m: 0 }}>
              {selectedParent?.fullName ?? (
                <Box component="span" sx={{ color: MUTED }}>
                  {t('contracts.drawer.preview.notChosen')}
                </Box>
              )}
            </Box>
            {discountRows.map(row => (
              <React.Fragment key={row.key}>
                <Box component="dt" sx={{ color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.label}
                </Box>
                <Box component="dd" sx={{ m: 0, color: 'success.main', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                  {row.text}
                </Box>
              </React.Fragment>
            ))}
            <Box component="dt" sx={{ color: MUTED }}>
              {monthly.discounted ? t('discounts.monthlyWithDiscount') : t('contracts.drawer.preview.monthly')}
            </Box>
            <Box component="dd" sx={{ m: 0, fontWeight: 600 }}>
              {monthlyText}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* footer */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ p: '16px 24px', borderTop: `1px solid ${palette.line}` }}
      >
        <Typography sx={{ fontSize: 12, color: MUTED }}>
          {ready ? t('contracts.drawer.footer.ready', { name: contract.childFullName }) : t('contracts.drawer.footer.incomplete')}
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', fontSize: 16, color: palette.text.primary, px: 2, borderRadius: '10px' }}>
            {t('common.cancel')}
          </Button>
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            label={t('contracts.drawer.footer.save')}
            onAsyncAction={handleSave}
            disabled={saving}
          />
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default ContractDrawer;
